import { ChangeEmailError, EnableMfaSmsError } from './accountSettings.enums'

// Single source for EnableMfaSms code -> locale key, shared by signup and account
// settings. Unknown (0) and unmapped codes intentionally have no entry.
export const ENABLE_MFA_SMS_ERROR_KEYS: Readonly<Record<number, string>> =
  Object.freeze({
    [EnableMfaSmsError.PhoneNotVerified]:
      'errors.codes.enableMfaSms.phoneNotVerified',
    [EnableMfaSmsError.TooManyAttempts]:
      'errors.codes.enableMfaSms.tooManyAttempts',
    [EnableMfaSmsError.AlreadyEnabled]:
      'errors.codes.enableMfaSms.alreadyEnabled',
    [EnableMfaSmsError.InvalidPhoneNumber]:
      'errors.codes.enableMfaSms.invalidPhoneNumber'
  })

// Returns the locale key for a coded EnableMfaSms business failure, or
// undefined for Unknown (0), null, and unmapped codes (generic error path).
export function getEnableMfaSmsErrorKey(
  errorCode: number | null | undefined
): string | undefined {
  return errorCode == null ? undefined : ENABLE_MFA_SMS_ERROR_KEYS[errorCode]
}

interface AccountSettingsErrorResponse {
  errorCode?: number | null
  errorMessage?: string | null
}

interface ErrorKeysByContext {
  [context: string]: Record<number, string>
}

const errorKeysByContext: ErrorKeysByContext = {
  updateEmail: {
    [ChangeEmailError.EmailAlreadyInUse]:
      'accountSettings.generalInfo.email.unavailableError',
    [ChangeEmailError.EmailMatchesExisting]:
      'accountSettings.generalInfo.email.sameEmailError',
    [ChangeEmailError.InvalidEmailFormat]:
      'accountSettings.generalInfo.email.invalidEmailError',
    [ChangeEmailError.EmailMissingOrEmpty]:
      'accountSettings.generalInfo.email.noEmailError'
  },
  sendVerificationCode: {
    1: 'accountSettings.generalInfo.phoneNumber.errors.invalid',
    2: 'accountSettings.generalInfo.phoneNumber.errors.tooManyAttempts'
  },
  enableMfaSms: ENABLE_MFA_SMS_ERROR_KEYS,
  disableMfaSms: { 1: 'accountSettings.generalInfo.mfa.errors.alreadyDisabled' }
}

export function getAccountSettingsErrorKey(
  context: string,
  response: AccountSettingsErrorResponse
): string {
  const codeKey =
    response.errorCode == null
      ? undefined
      : errorKeysByContext[context]?.[response.errorCode]

  return codeKey ?? 'accountSettings.common.unexpectedError'
}
